import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, ButtonLink, Notice, Panel } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import { peekBriefUnsubscribe, unsubscribeFromBrief } from "@/lib/brief";
import { SEO } from "@/lib/seo";
import "@/styles/phase2-demand.css";

export default function BriefUnsubscribe() {
  const { token = "" } = useParams<{ token: string }>();
  const { city } = useCity();
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const peek = useQuery({
    queryKey: ["brief-unsubscribe", city.slug, token],
    queryFn: () => peekBriefUnsubscribe(city.slug, token),
    enabled: Boolean(token),
    retry: false,
  });

  const unsubscribe = async () => {
    setState("submitting");
    try {
      await unsubscribeFromBrief(city.slug, token);
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <FocusShell>
      <SEO
        title="Unsubscribe from the Canberra Referral Brief · PulseBoard"
        description="Stop receiving the Canberra Referral Brief."
        path={`/brief/unsubscribe/${token}`}
        noindex
      />
      <Panel>
        <p className="ds-eyebrow ds-eyebrow--teal">{city.name} Referral Brief</p>
        {peek.isLoading ? <p className="ds-small" role="status">Checking your unsubscribe link…</p> : null}
        {peek.isError ? (
          <div className="ds-stack" style={{ gap: 8 }} role="alert">
            <h1 className="ds-display-2">This unsubscribe link has expired</h1>
            <p className="ds-small">If you still receive the brief, email support@pulseboard.com.au and we will remove you.</p>
          </div>
        ) : null}
        {peek.data && state !== "success" ? (
          <div className="ds-stack" style={{ gap: 14 }}>
            <h1 className="ds-display-2">Stop the weekly brief?</h1>
            <p className="ds-body">This unsubscribes <strong>{peek.data.email_masked}</strong>.</p>
            <div>
              <Button variant="secondary" disabled={state === "submitting"} onClick={() => void unsubscribe()}>
                {state === "submitting" ? "Unsubscribing…" : "Unsubscribe"}
              </Button>
            </div>
            {state === "error" ? <Notice tone="error">The subscription didn't update. Try again in a moment.</Notice> : null}
          </div>
        ) : null}
        {state === "success" ? (
          <div className="ds-stack" style={{ gap: 8 }} role="status">
            <h1 className="ds-display-2">You're unsubscribed</h1>
            <p className="ds-body">No more referral briefs will be sent to this address.</p>
          </div>
        ) : null}
        <div><ButtonLink to="/" variant="ghost">Back to PulseBoard</ButtonLink></div>
      </Panel>
    </FocusShell>
  );
}
